<template>
  <AdaptiveSheet
    :opened="postPopupOpened"
    class="feed-manage-post"
    :tablet-fullscreen="false"
    swipe-to-close
    backdrop
    close-by-backdrop-click
    @closed="onPostPopupClosed"
  >
    <f7-view>
      <f7-page name="feed-edit-post">
        
            <f7-navbar>
              <f7-nav-title>{{ $t("feed.editPost") }}</f7-nav-title>
              <f7-nav-right>
                <f7-link sheet-close popup-close
                  ><f7-icon f7="multiply" size="18"
                /></f7-link>
              </f7-nav-right>
            </f7-navbar>
            <div class="mfd-body">
              <PostEditor
                :key="'feed-post-' + (editingItem?.id || '')"
                :editing="true"
                :initial-headline="editingItem?.headline || ''"
                :initial-text="editingItem?.text || ''"
                :creating="savingPost"
                :submit-label="$t('feed.updatePost')"
                @submit="onPostSubmit"
              />
            </div>
     
      </f7-page>
    </f7-view>
  </AdaptiveSheet>

  <AdaptiveSheet
    :opened="pollPopupOpened"
    class="feed-manage-poll"
    swipe-to-close
    backdrop
    close-by-backdrop-click
    @closed="onPollPopupClosed"
  >
    <f7-view>
      <f7-page name="feed-edit-poll">
      
            <f7-navbar>
              <f7-nav-title>{{ $t("feed.editPoll") }}</f7-nav-title>
              <f7-nav-right>
                <f7-link sheet-close popup-close
                  ><f7-icon f7="multiply" size="18"
                /></f7-link>
              </f7-nav-right>
            </f7-navbar>
            <div class="mfd-body">
              <PollCreate
                :opened="pollPopupOpened"
                :editing="true"
                :poll-to-edit="editingItem"
                @poll-updated="onPollUpdated"
              />
            </div>
       
      </f7-page>
    </f7-view>
  </AdaptiveSheet>
</template>

<script setup>
import { ref } from "vue";
import { f7 } from "framework7-vue";
import PostEditor from "./PostEditor.vue";
import PollCreate from "../pollCreate.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import { useAuthStore } from "../../js/pinia-stores/auth.js";
import { usePostStore } from "../../js/pinia-stores/post.js";
import { usePollStore } from "../../js/pinia-stores/poll.js";
import {
  splitUploadedDocs,
  seedDocumentsFromMedia,
} from "../../js/feedManageUtils.js";
import i18n from "../../js/i18n.js";

const emit = defineEmits(["updated", "deleted"]);

const authStore = useAuthStore();
const postStore = usePostStore();
const pollStore = usePollStore();

const editingItem = ref(null);
const postPopupOpened = ref(false);
const pollPopupOpened = ref(false);
const savingPost = ref(false);

function openEdit(item) {
  editingItem.value = item;
  if (item.type === "POLL" || item.type === "SPONSORED_POLL") {
    pollPopupOpened.value = true;
  } else {
    authStore.clearDocumentsUrl();
    authStore.documentsUrl = seedDocumentsFromMedia(item.media || []);
    postPopupOpened.value = true;
  }
}

function onPostPopupClosed() {
  postPopupOpened.value = false;
  authStore.clearDocumentsUrl();
}

function onPollPopupClosed() {
  pollPopupOpened.value = false;
  authStore.clearDocumentsUrl();
}

async function onPostSubmit({ headline, text }) {
  const item = editingItem.value;
  if (!item) return;
  savingPost.value = true;
  try {
    const shortId = f7.utils.id();
    const { existing, fresh } = splitUploadedDocs(authStore.documentsUrl);
    if (fresh.length > 0) {
      authStore.documentsUrl = fresh;
      await authStore.postR2Document({
        shortId,
        bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
      });
    }
    const finalDocs = [...existing, ...authStore.documentsUrl];
    const media = finalDocs.map((d) => d.url).filter(Boolean);
    const payload = {
      headline,
      text,
      media,
      attachments: finalDocs.map(({ file, ...rest }) => rest),
    };
    await postStore.updatePost({ postId: item.id, post: payload });
    emit("updated", { ...item, headline, text, media });
    authStore.clearDocumentsUrl();
    postPopupOpened.value = false;
    f7.toast.show({
      text: i18n.global.t("feed.postUpdated"),
      position: "center",
      closeTimeout: 1500,
    });
  } catch (err) {
    console.error("feed edit post error", err);
    f7.toast.show({ text: i18n.global.t("common.error"), position: "center" });
  } finally {
    savingPost.value = false;
  }
}

function onPollUpdated(updatedPoll) {
  const item = editingItem.value;
  if (!item) return;
  const poll = {
    ...item.poll,
    ...updatedPoll,
    id: item.poll?.id || updatedPoll?.id,
  };
  emit("updated", {
    ...item,
    headline: updatedPoll?.title || item.headline,
    text: updatedPoll?.description || item.text,
    poll,
  });
  authStore.clearDocumentsUrl();
  pollPopupOpened.value = false;
}

function confirmDelete(item) {
  const isPoll = item.type === "POLL" || item.type === "SPONSORED_POLL";
  f7.dialog.confirm(
    i18n.global.t(isPoll ? "feed.deletePollConfirm" : "feed.deletePostConfirm"),
    i18n.global.t("feed.delete"),
    async () => {
      try {
        if (isPoll) {
          await pollStore.deletePoll(item.poll?.id || item.id);
        } else {
          await postStore.deletePost(item.id);
        }
        emit("deleted", item.id);
        f7.toast.show({
          text: i18n.global.t("feed.deleted"),
          position: "center",
          closeTimeout: 1500,
        });
      } catch (err) {
        console.error("feed delete error", err);
        f7.toast.show({
          text: i18n.global.t("common.error"),
          position: "center",
        });
      }
    },
    () => {},
  );
}

defineExpose({ openEdit, confirmDelete });
</script>

<style scoped>
.mfd-body {
  padding: 16px;
}
</style>
